'use client';

import { ReactNode, useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon, IconName } from '~/components/core/Icon';
import Text from '~/components/customComponents/texts/Text';
import { ColorModeEnum } from '~/utils/generalUtils';
import { colors, spacing } from '~/utils/tailwindUtils';
import Flex from '../flex/Flex';

export interface CounterFormFieldProps {
  value: number | string;
  onChange?: (value: any) => void;
  name?: string;
  classNames?: string;
  multiplier?: number;
  isDisabled?: boolean;
  doNotDisableDecreaseButton?: boolean;
  leftItem?: ReactNode;
  rightItem?: ReactNode;
  counterInputFieldClassName?: string;
  counterInputContainerClassName?: string;
  decreseButtonClassName?: string;
  increaseButtonClassName?: string;
}

/**
 * Counter component that is used to display a counter with a plus and minus button to increase and decrease the counter value respectively
 * @param name - The name of the counter
 * @param onChange - The function to call when the counter value is changed
 * @param value - The value of the counter
 * @param classNames - The classes to be applied to the counter
 * @param multiplier - The multiplier to be used when increasing or decreasing the counter value
 * @returns Counter component
 */
export const Counter = ({
  name,
  onChange = () => {},
  value,
  classNames,
  counterInputFieldClassName,
  counterInputContainerClassName,
  doNotDisableDecreaseButton,
  decreseButtonClassName,
  increaseButtonClassName,
  multiplier,
  isDisabled = false,
  leftItem,
  rightItem,
}: Omit<CounterFormFieldProps, 'label'>) => {
  const [inputValue, setInputValue] = useState<number>(Number(value));
  const multiplierOr1 = multiplier || 1;

  // Update input value when the `value` prop changes
  useEffect(() => {
    if (Number(value) !== inputValue) setInputValue(Number(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Handle value changes and trigger API request
  const triggerAPIRequest = useCallback((newValue: number) => onChange(newValue), [onChange]);

  // Handle input change
  const handleInputChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = evt.target.value;
    if (newValue === '' || /^[0-9]*$/.test(newValue)) {
      setInputValue(Number(newValue));
    }
  }, []);

  const handleKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
      evt.stopPropagation();

      // Simulate handleInputChange behavior
      const newValue = evt.currentTarget.value;
      if (/^[0-9]*$/.test(newValue)) {
        triggerAPIRequest(Number(newValue));
      }
    }
  };

  // Handle increase button click
  const handleIncrease = useCallback(() => {
    const newValue = inputValue + multiplierOr1;
    triggerAPIRequest(newValue);
  }, [inputValue, multiplierOr1, triggerAPIRequest]);

  // Handle decrease button click
  const handleDecrease = useCallback(() => {
    const newValue = doNotDisableDecreaseButton
      ? Math.max(inputValue - multiplierOr1, 0)
      : Math.max(inputValue - multiplierOr1, multiplierOr1);
    triggerAPIRequest(newValue);
  }, [inputValue, multiplierOr1, triggerAPIRequest, doNotDisableDecreaseButton]);

  // Determine if the decrease button should be disabled
  const isDecreaseDisabled = inputValue === multiplierOr1;

  return (
    <Flex
      className={twMerge('bg-gray-50 rounded-lg', isDisabled && 'pointer-events-none', classNames)}
    >
      {/* Decrease Button */}
      <Flex
        className={twMerge(
          'cursor-pointer items-center gap-1.5 px-4 py-3',
          doNotDisableDecreaseButton
            ? ''
            : (isDecreaseDisabled || isDisabled) && 'opacity-50 cursor-not-allowed',
          decreseButtonClassName,
        )}
        onClick={handleDecrease}
        data-testid="decrease-button"
      >
        <Icon
          name={IconName.Minus}
          width={spacing[3]}
          height={spacing[4]}
          color={colors.gray[500]}
          customStrokeWidth={6}
          colorMode={ColorModeEnum.Fill}
          className={twMerge('pt-px')}
        />
        {multiplierOr1 > 1 && <Text className="text-gray-500 font-medium">{multiplier}</Text>}
      </Flex>

      {/* Input Field */}
      <Flex
        className={twMerge(
          'items-center justify-center gap-2 w-full md:w-fit border-r-2 border-r-white border-l-2 border-l-white',
          counterInputContainerClassName,
        )}
      >
        {leftItem && leftItem}
        <input
          name={name}
          id={name}
          type="text" // Change to text for better control
          inputMode="numeric" // Shows numeric keyboard on mobile
          pattern="[0-9]*" // Enforces numeric input
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={(e) => triggerAPIRequest(Number(e.target.value))}
          className={twMerge(
            'text-black text-center text-sm md:text-base font-medium w-full focus:outline-none bg-gray-50',
            counterInputFieldClassName,
          )}
          disabled={isDisabled}
          aria-disabled={isDisabled}
        />
        {rightItem && rightItem}
      </Flex>

      {/* Increase Button */}
      <Flex
        className={twMerge(
          'cursor-pointer justify-end items-center gap-1 px-4 py-3',
          isDisabled && 'cursor-not-allowed',
          increaseButtonClassName,
        )}
        onClick={handleIncrease}
        data-testid="increase-button"
      >
        <Icon
          name={IconName.Add}
          size={spacing[4]}
          color={colors.gray[500]}
          colorMode={ColorModeEnum.Fill}
          customStrokeWidth={3}
        />
        {multiplierOr1 > 1 && (
          <Text color="lightGray" fontWeight="medium">
            {multiplier}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};
